import { useMemo } from 'react'
import { Button } from '@/atoms/Button'
import { TitleMD } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { Input, TextArea } from '../../components/Inputs'
import { Page } from '../../components/Page'
import { PageSection } from '../../components/PageSection'
import {
  TorchApplicationData,
  TorchApplicationGenre,
  useTorchApplication,
} from '../../components/TorchApplicationContext'
import { FilmAcknowledgement } from './FilmAcknowledgement'
import { PresentationTypes } from './PresentationTypes'
import { ProductionTypes } from './ProductionTypes'
import { TorchAges } from './TorchAges'
import { TorchGenres } from './TorchGenres'

const requiredFields = ['title', 'description', 'productionType', 'presentationType', 'genres']

const MAX_DESCRIPTION_LENGTH = 2000

type Page2FullFilmProps = {
  data: TorchApplicationData
  onSaveData: (data: { name: string; value: string | string[] | boolean }) => void
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleInputBlur: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
}

export const Page2FullFilm = ({ data, handleInputBlur, handleInputChange, onSaveData }: Page2FullFilmProps) => {
  const { setPage, isDraft } = useTorchApplication()
  const { t } = useTranslate('guild-create')

  const errors = useMemo(() => {
    const errors = {} as Record<string, string>
    if (data.description && data.description.length > MAX_DESCRIPTION_LENGTH) {
      errors.description = t('charactersOverLimit', '{{ chars }} characters over limit', {
        chars: (data.description.length - MAX_DESCRIPTION_LENGTH).toLocaleString(),
      })
    }

    return errors
  }, [t, data])

  const requiredFieldsAnswered = requiredFields.every((field) => !!data[field as keyof TorchApplicationData])
  const genreAgesAnswered = data.genres?.includes(TorchApplicationGenre.Children) ? data.ages?.length : true

  const canProceed = requiredFieldsAnswered && genreAgesAnswered && !Object.keys(errors).length
  const disabled = !data?.torchAcknowledgement || !isDraft

  return (
    <>
      <Page disabled={!canProceed} onBack={() => setPage(1)} onNext={() => setPage(3)}>
        <FilmAcknowledgement />
        <PageSection>
          <TitleMD weight="bold">{t('filmDetails', 'Film Details')}</TitleMD>
          <Input
            label={t('filmTitle', 'Film Title')}
            value={data.title || ''}
            onChange={handleInputChange}
            name="title"
            disabled={disabled}
            required
            onBlur={handleInputBlur}
          />

          <TextArea
            label={t('filmDescription', 'Film Description')}
            value={data.description || ''}
            name="description"
            onChange={handleInputChange}
            disabled={disabled}
            required
            onBlur={handleInputBlur}
            error={!!errors.description}
            helperText={
              errors.description || (MAX_DESCRIPTION_LENGTH - (data.description?.length || 0)).toLocaleString()
            }
            isValid={!errors.description}
            rows={5}
          />
          <ProductionTypes
            disabled={disabled}
            value={data.productionType}
            onChange={onSaveData}
            name="productionType"
          />
          <PresentationTypes
            disabled={disabled}
            value={data.presentationType}
            onChange={onSaveData}
            name="presentationType"
          />
          <TorchGenres disabled={disabled} value={data.genres} onChange={onSaveData} name="genres" />
          {data?.genres?.includes(TorchApplicationGenre.Children) && (
            <TorchAges disabled={disabled} value={data.ages} onChange={onSaveData} name="ages" />
          )}
        </PageSection>

        <Button variant="oxide" disabled={!canProceed} onClick={() => setPage(3)} className="w-72">
          {t('continue', 'Continue')}
        </Button>
      </Page>
    </>
  )
}
